import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandExpandContext,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Spinner,
} from '@hap/components'

import { Avatar } from '../avatar'
import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function UsersCommandList<T extends boolean = false>({
  selected = [],
  placeholder = 'Filter Members...',
  ...props
}: Props<T, string>) {
  const [condensedList, setCondensedList] = useState(true)
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Users)
  const hasUsers: Record<string, number> =
    data?.reduce<Record<string, number>>((acc, user) => {
      if (user.meta.reportsTo) {
        const subordinates = acc[user.meta.reportsTo] || 0
        acc[user.meta.reportsTo] = subordinates + 1
      }
      return acc
    }, {}) || {}

  const filteredList = useMemo(() => {
    return condensedList
      ? data?.filter((user) => {
          if (props.excludeIds) return !props.excludeIds.includes(user.id)
          if (!props.contextFilters) return true
          const { reportsTo, subordinates, id } = props.contextFilters
          if (subordinates && '$exists' in subordinates) return hasUsers[user.id]
          if (reportsTo && '$ne' in reportsTo) return !reportsTo.$ne.includes(user.id)
          if (id && '$ne' in id) return !id.$ne.includes(user.id)
          return true
        })
      : data
  }, [data, condensedList, props.excludeIds, props.contextFilters, hasUsers])

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return filteredList
    const normalizedQuery = query.toLowerCase()
    return filteredList?.filter(
      (user) =>
        user.name.toLowerCase().includes(normalizedQuery) ||
        user.meta.email?.toLowerCase().includes(normalizedQuery),
    )
  }, [filteredList, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      props.onSelect(newSelection)
    },
    [props.onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (props.multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        props.onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        props.onSelect([id])
      }
    },
    [props.multiselect, selected, props.onSelect],
  )

  const handleClear = useCallback(() => {
    props.onSelect([])
  }, [props.onSelect])

  const expandedResults = (data?.length || 0) - (filteredList?.length || 0)
  return isInitialLoading ? (
    <div className="flex aspect-square items-center justify-center">
      <Spinner size="40" />
    </div>
  ) : (
    <Command>
      <CommandInput
        placeholder={placeholder}
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={props.multiselect}
          />
          {props.allowEmpty && (
            <CommandItem onSelect={handleClear} className="gap-2">
              <Icon name="x" />
              {props.allowEmptyLabel || 'Clear'}
            </CommandItem>
          )}
          {visibleItems?.map((user) => (
            <CommandCheckItem
              key={user.id}
              value={user.id}
              keywords={[user.name, user.meta.email]}
              className="group mx-1 flex gap-3"
              selected={selected.includes(user.id)}
              onSelect={() => handleToggleItem(user.id)}
              multiselect={props.multiselect}
            >
              <Avatar {...user.meta} size="sm" />
              <span className="truncate">{user.name}</span>
            </CommandCheckItem>
          ))}
          <CommandExpandContext
            availableResults={expandedResults}
            onSelect={() => setCondensedList(false)}
            optionalLabel="member"
          />
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

export interface UserContextFilters {
  id?: {
    $ne: string[]
  }
  reportsTo?: {
    $ne: string[]
  }
  subordinates?: {
    $exists: true
  }
}

interface Props<
  T extends boolean | undefined,
  V extends string,
> extends BaseCommandListProps<T, V> {
  contextFilters?: UserContextFilters
}
